// Sprint 6 (Phase 5, D39) — Sticky progress bar.
//
// Always visible above the message list when the active agent has at least
// one TodoWrite emit. Collapsed: one-line "Phase N of M · current item" +
// thin progress bar. Expanded: full plan list using the existing
// `TodoChecklist` shell.
//
// Sticky positioning pins the bar at the top of the scroll container (top-0).
//
// Sprint sticky-message-section-stacking-cursor-parity:
//   • z-index bumped from z-[6] to z-30 so this bar covers the user
//     message stickies (z-20) at the top of the viewport. Pre-sprint it
//     was backwards (user sticky on top of the bar in a 1 px sliver).
//   • Reports its measured height to the scroll container via the CSS
//     custom property `--user-sticky-top`. UserMessage stickies read
//     that property as their `top:` offset, so they dock just below
//     the progress bar when it's visible, and snap to `top: 0` when
//     it's absent. Pre-sprint the same coordination happened through
//     React state + an IntersectionObserver `rootMargin` — now it's
//     pure CSS and the state churn is gone.

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import type { ProgressState } from '../../lib/tool-rendering/derive-progress';

export interface StickyProgressBarProps {
  progress: ProgressState | null;
}

export function StickyProgressBar({ progress }: StickyProgressBarProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  // Sprint sticky-message-section-stacking-cursor-parity — write the
  // measured height of the bar to a CSS custom property on the nearest
  // scroll container so UserMessage stickies can dock below it. Reset
  // the property to 0 px when the bar is unmounted or invisible.
  //
  // We stash the scroll container in a ref the moment the bar mounts,
  // so the cleanup path can reset `--user-sticky-top` even after the
  // bar has been unmounted (when `progress` transitions to null, React
  // removes the bar div before the effect cleanup runs — `barRef.current`
  // is null at that point, but `scrollContainerRef.current` survives).
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) {
      // Bar is not mounted this render (progress === null). Reset the
      // property on the previously-known scroll container so user
      // stickies snap back to top: 0.
      const prev = scrollContainerRef.current;
      if (prev) prev.style.setProperty('--user-sticky-top', '0px');
      return;
    }
    const scrollContainer = bar.closest('[data-testid="message-list"]');
    if (!(scrollContainer instanceof HTMLElement)) return;
    scrollContainerRef.current = scrollContainer;
    const update = () => {
      scrollContainer.style.setProperty(
        '--user-sticky-top',
        `${bar.offsetHeight}px`,
      );
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        scrollContainer.style.setProperty('--user-sticky-top', '0px');
      };
    }
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      scrollContainer.style.setProperty('--user-sticky-top', '0px');
    };
  }, [progress]);

  if (!progress) return null;

  const pct = Math.round(progress.pct * 100);
  const headline =
    progress.inProgressItem ??
    (progress.current === progress.total ? t('progress.allDone') : t('progress.working'));

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border"
      data-testid="sticky-progress-bar"
      data-pct={pct}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-accent/40 focus:outline-none"
        data-testid="sticky-progress-header"
      >
        <span className="text-caption text-muted-foreground shrink-0">
          {t('progress.phaseOf', {
            current: progress.current + (progress.inProgressItem ? 1 : 0),
            total: progress.total,
          })}
        </span>
        <span className="text-body text-foreground flex-1 truncate">· {headline}</span>
        <span className="text-caption text-muted-foreground/45 tabular-nums">{pct}%</span>
        <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
          {open ? <Icon name="ChevronDown" size={14} /> : <Icon name="ChevronRight" size={14} />}
        </span>
      </button>
      <div
        className="h-1 w-full bg-border/50 overflow-hidden"
        data-testid="sticky-progress-track"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-status-success transition-[width] duration-300"
          style={{ width: `${pct}%` }}
          data-testid="sticky-progress-fill"
        />
      </div>
      {open && (
        <ul
          className="px-4 py-2 space-y-1 text-body text-muted-foreground max-h-[40vh] overflow-y-auto"
          data-testid="sticky-progress-list"
        >
          {progress.fullList.map((t, i) => {
            const meta = STATUS_META[t.status];
            return (
              <li
                key={i}
                className={`flex items-start gap-2 ${meta.line}`}
                data-status={t.status}
              >
                <span
                  className={`mt-0.5 inline-flex w-4 h-4 items-center justify-center text-caption ${meta.iconClass}`}
                  aria-hidden="true"
                >
                  {meta.icon}
                </span>
                <span className={`flex-1 ${meta.text}`}>
                  {t.content}
                  {t.status === 'in_progress' && t.activeForm && (
                    <span className="block text-caption text-status-success/80 mt-0.5">
                      {t.activeForm}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const STATUS_META = {
  pending: {
    icon: '○',
    iconClass: 'text-muted-foreground/45',
    text: 'text-muted-foreground',
    line: '',
  },
  in_progress: {
    icon: '◐',
    iconClass: 'text-status-success',
    text: 'text-foreground font-medium',
    line: '',
  },
  completed: {
    icon: '✓',
    iconClass: 'text-status-success',
    text: 'text-muted-foreground line-through',
    line: '',
  },
  cancelled: {
    icon: '✕',
    iconClass: 'text-muted-foreground/45',
    text: 'text-muted-foreground/45 line-through',
    line: '',
  },
} as const;
